import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CompanyStorage } from '../_core/company-storage';
import { TenantAuthService } from './tenant-auth.service';

export type NotificationSound =
  | 'none'
  | 'default'
  | 'bell'
  | 'chime'
  | 'alert'
  | 'new-order';

interface NotificationTone {
  frequency: number;
  offset: number;
  duration: number;
  type: OscillatorType;
  volume: number;
}

const NOTIFICATION_SOUND_STORAGE_KEY = '__notification_sound';
const DEFAULT_NOTIFICATION_SOUND: NotificationSound = 'default';

@Injectable({ providedIn: 'root' })
export class NotificationSoundService {
  private readonly _selectedSound$ =
    new BehaviorSubject<NotificationSound>(DEFAULT_NOTIFICATION_SOUND);
  private readonly _ready: Promise<void>;
  private _audioContext: AudioContext | null = null;

  get selectedSound(): NotificationSound {
    return this._selectedSound$.value;
  }

  constructor(
    private _companyStorage: CompanyStorage,
    private _tenantAuth: TenantAuthService,
  ) {
    this._ready = this._loadSelectedSound();
  }

  async setSound(sound: NotificationSound): Promise<void> {
    if (!this._isNotificationSound(sound)) {
      return;
    }

    await this._ready;
    this._selectedSound$.next(sound);
    if (this._tenantAuth.currentTenant) {
      await this._companyStorage.set(NOTIFICATION_SOUND_STORAGE_KEY, sound);
    }
    await this.play();
  }

  async play(): Promise<void> {
    await this._ready;

    if (this.selectedSound === 'none') {
      return;
    }

    if (this.selectedSound === 'new-order') {
      await this._playNewOrderSound();
      return;
    }

    const context = this._getAudioContext();
    if (!context) {
      await this._playNewOrderSound();
      return;
    }

    await context.resume().catch(() => undefined);
    const startAt = context.currentTime + 0.02;

    for (const tone of this._getPattern(this.selectedSound)) {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      const toneStart = startAt + tone.offset;
      const toneEnd = toneStart + tone.duration;

      oscillator.type = tone.type;
      oscillator.frequency.setValueAtTime(tone.frequency, toneStart);
      gain.gain.setValueAtTime(0.0001, toneStart);
      gain.gain.exponentialRampToValueAtTime(
        tone.volume,
        toneStart + 0.015,
      );
      gain.gain.exponentialRampToValueAtTime(0.0001, toneEnd);

      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start(toneStart);
      oscillator.stop(toneEnd + 0.02);
    }
  }

  private async _loadSelectedSound(): Promise<void> {
    if (!this._tenantAuth.currentTenant) {
      return;
    }

    try {
      const stored = await this._companyStorage.get(
        NOTIFICATION_SOUND_STORAGE_KEY,
      );
      if (this._isNotificationSound(stored)) {
        this._selectedSound$.next(stored);
      }
    } catch (error) {
      console.warn('Failed to load notification sound', error);
    }
  }

  private _getAudioContext(): AudioContext | null {
    if (this._audioContext) {
      return this._audioContext;
    }

    const AudioContextConstructor =
      window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextConstructor) {
      return null;
    }

    this._audioContext = new AudioContextConstructor();
    return this._audioContext;
  }

  private async _playNewOrderSound(): Promise<void> {
    const audio = new Audio('assets/audio/new_order_sound.mp3');
    await audio.play().catch(() => undefined);
  }

  private _getPattern(sound: NotificationSound): NotificationTone[] {
    switch (sound) {
      case 'bell':
        return [
          {
            frequency: 1046,
            offset: 0,
            duration: 0.5,
            type: 'sine',
            volume: 0.18,
          },
          {
            frequency: 2093,
            offset: 0,
            duration: 0.35,
            type: 'sine',
            volume: 0.06,
          },
        ];
      case 'chime':
        return [
          {
            frequency: 659,
            offset: 0,
            duration: 0.18,
            type: 'sine',
            volume: 0.14,
          },
          {
            frequency: 784,
            offset: 0.16,
            duration: 0.18,
            type: 'sine',
            volume: 0.14,
          },
          {
            frequency: 1047,
            offset: 0.32,
            duration: 0.3,
            type: 'sine',
            volume: 0.14,
          },
        ];
      case 'alert':
        return [
          {
            frequency: 740,
            offset: 0,
            duration: 0.14,
            type: 'square',
            volume: 0.07,
          },
          {
            frequency: 520,
            offset: 0.16,
            duration: 0.14,
            type: 'square',
            volume: 0.07,
          },
          {
            frequency: 740,
            offset: 0.32,
            duration: 0.18,
            type: 'square',
            volume: 0.07,
          },
        ];
      default:
        return [
          {
            frequency: 880,
            offset: 0,
            duration: 0.16,
            type: 'sine',
            volume: 0.14,
          },
          {
            frequency: 660,
            offset: 0.18,
            duration: 0.22,
            type: 'sine',
            volume: 0.14,
          },
        ];
    }
  }

  private _isNotificationSound(value: unknown): value is NotificationSound {
    return (
      value === 'none' ||
      value === 'default' ||
      value === 'bell' ||
      value === 'chime' ||
      value === 'alert' ||
      value === 'new-order'
    );
  }
}
